"use client";

import { Bot, Loader2, Send, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "~/lib/auth-context";
import { CHATBOT_MESSAGES_STORAGE_KEY } from "~/lib/storage-keys";
import { trpc } from "~/utils/trpc";
import { Button } from "@xeito/ui";
import { cn } from "~/utils/cn";

interface Message {
	id: string;
	role: "user" | "assistant";
	content: string;
	timestamp: number;
}

const QUICK_ACTIONS = [
	{ id: "pricing", labelKey: "quickActions.pricing" },
	{ id: "features", labelKey: "quickActions.features" },
	{ id: "how-it-works", labelKey: "quickActions.howItWorks" },
	{ id: "ai-features", labelKey: "quickActions.aiFeatures" },
];

// Translations for all 13 supported languages
const translations: Record<
	string,
	{
		title: string;
		welcomeMessage: string;
		inputPlaceholder: string;
		typing: string;
		errorMessage: string;
		clearConversation: string;
		minimize: string;
		askAnything: string;
		messagesLeft: string;
		limitReached: string;
		quickActions: Record<string, string>;
	}
> = {
	en: {
		title: "Xeito AI Assistant",
		welcomeMessage:
			"Hi! I'm the Xeito AI Assistant. I can help with questions about Xeito: how it works, plans and pricing, features and your account. For your CV, cover letters, interview practice or salary negotiation, use Xeito's AI tools.",
		inputPlaceholder: "Ask about Xeito...",
		askAnything: "I answer questions about Xeito. For CV, cover letters, interviews and salary, use the AI tools.",
		messagesLeft: "messages left today",
		limitReached: "You've used today's messages. They reset tomorrow.",
		typing: "Thinking...",
		errorMessage:
			"I'm sorry, I'm having trouble answering your question right now. Please try again in a moment.",
		clearConversation: "Clear conversation",
		minimize: "Minimize",
		quickActions: {
			pricing: "What are the pricing plans?",
			features: "What features do you offer?",
			howItWorks: "How does it work?",
			aiFeatures: "What AI tools and agents are available?",
		},
	},
	es: {
		title: "Asistente IA de Xeito",
		welcomeMessage:
			"¡Hola! Soy el Asistente IA de Xeito. Te ayudo con dudas sobre Xeito: cómo funciona, planes y precios, funciones y tu cuenta. Para tu CV, cartas de presentación, práctica de entrevistas o negociación salarial, usa las herramientas de IA de Xeito.",
		inputPlaceholder: "Pregunta sobre Xeito...",
		typing: "Pensando...",
		errorMessage:
			"Lo siento, tengo problemas para responder a tu pregunta en este momento. Por favor, inténtalo de nuevo.",
		clearConversation: "Borrar conversación",
		minimize: "Minimizar",
		askAnything: "Respondo dudas sobre Xeito. Para CV, cartas, entrevistas y salario, usa las herramientas de IA.",
		messagesLeft: "mensajes restantes hoy",
		limitReached: "Has usado los mensajes de hoy. Se renuevan mañana.",
		quickActions: {
			pricing: "¿Cuáles son los planes de precios?",
			features: "¿Qué funcionalidades ofrecen?",
			howItWorks: "¿Cómo funciona?",
			aiFeatures: "¿Qué herramientas y agentes de IA hay disponibles?",
		},
	},
	ca: {
		title: "Assistenta IA de Xeito",
		welcomeMessage:
			"Hola! Soc l'Assistenta IA de Xeito. T'ajudo amb dubtes sobre Xeito: com funciona, plans i preus, funcions i el teu compte. Per al CV, cartes de presentació, pràctica d'entrevistes o negociació salarial, fes servir les eines d'IA de Xeito.",
		inputPlaceholder: "Pregunta sobre Xeito...",
		typing: "Pensant...",
		errorMessage:
			"Ho sento, tinc problemes per respondre la teva pregunta ara mateix. Si us plau, intenta-ho de nou.",
		clearConversation: "Esborrar conversa",
		minimize: "Minimitzar",
		askAnything: "Responc dubtes sobre Xeito. Per a CV, cartes, entrevistes i salari, fes servir les eines d'IA.",
		messagesLeft: "missatges restants avui",
		limitReached: "Has fet servir els missatges d'avui. Es renoven demà.",
		quickActions: {
			pricing: "Quins són els plans de preus?",
			features: "Quines funcionalitats ofereixen?",
			howItWorks: "Com funciona?",
			aiFeatures: "Quines eines i agents d'IA hi ha disponibles?",
		},
	},
	de: {
		title: "Xeito-KI-Assistent",
		welcomeMessage:
			"Hallo! Ich bin der Xeito-KI-Assistent. Ich helfe dir bei Fragen zu Xeito: wie es funktioniert, Pläne und Preise, Funktionen und dein Konto. Für Lebenslauf, Anschreiben, Interview-Training oder Gehaltsverhandlung nutze die KI-Tools von Xeito.",
		inputPlaceholder: "Frag mich zu Xeito...",
		typing: "Denken...",
		errorMessage:
			"Entschuldigung, ich habe gerade Schwierigkeiten, deine Frage zu beantworten. Bitte versuch es noch einmal.",
		clearConversation: "Gespräch löschen",
		minimize: "Minimieren",
		askAnything: "Ich beantworte Fragen zu Xeito. Für Lebenslauf, Anschreiben, Interviews und Gehalt nutze die KI-Tools.",
		messagesLeft: "Nachrichten heute noch übrig",
		limitReached: "Du hast die Nachrichten für heute aufgebraucht. Morgen geht es weiter.",
		quickActions: {
			pricing: "Was sind die Preismodelle?",
			features: "Welche Funktionen bieten Sie an?",
			howItWorks: "Wie funktioniert es?",
			aiFeatures: "Welche KI-Tools und Agenten sind verfügbar?",
		},
	},
	fr: {
		title: "Assistant IA Xeito",
		welcomeMessage:
			"Bonjour ! Je suis l'Assistant IA Xeito. Je réponds à vos questions sur Xeito : son fonctionnement, les offres et tarifs, les fonctionnalités et votre compte. Pour votre CV, vos lettres de motivation, l'entraînement aux entretiens ou la négociation salariale, utilisez les outils d'IA de Xeito.",
		inputPlaceholder: "Posez une question sur Xeito...",
		typing: "Réflexion...",
		errorMessage:
			"Désolé, j'ai des difficultés à répondre à votre question pour le moment. Veuillez réessayer.",
		clearConversation: "Effacer la conversation",
		minimize: "Minimiser",
		askAnything: "Je réponds aux questions sur Xeito. Pour CV, lettres, entretiens et salaire, utilisez les outils d'IA.",
		messagesLeft: "messages restants aujourd'hui",
		limitReached: "Vous avez utilisé les messages d'aujourd'hui. Ils se renouvellent demain.",
		quickActions: {
			pricing: "Quels sont les tarifs?",
			features: "Quelles fonctionnalités proposez-vous?",
			howItWorks: "Comment ça fonctionne?",
			aiTools: "Quels outils d'IA sont disponibles?",
		},
	},
	gl: {
		title: "Asistente IA de Xeito",
		welcomeMessage:
			"Ola! Son o Asistente IA de Xeito. Axúdoche con dúbidas sobre Xeito: como funciona, plans e prezos, funcións e a túa conta. Para o teu CV, cartas de presentación, práctica de entrevistas ou negociación salarial, usa as ferramentas de IA de Xeito.",
		inputPlaceholder: "Pregunta sobre Xeito...",
		typing: "Pensando...",
		errorMessage:
			"Sinto ter problemas para responder á túa pregunta neste momento. Por favor, téntao de novo.",
		clearConversation: "Borrar conversa",
		minimize: "Minimizar",
		askAnything: "Respondo dúbidas sobre Xeito. Para CV, cartas, entrevistas e salario, usa as ferramentas de IA.",
		messagesLeft: "mensaxes restantes hoxe",
		limitReached: "Usaches as mensaxes de hoxe. Renóvanse mañá.",
		quickActions: {
			pricing: "Cales son os plans de prezos?",
			features: "Que funcionalidades ofrecen?",
			howItWorks: "Como funciona?",
			aiTools: "Que ferramentas de IA hai dispoñibles?",
		},
	},
	it: {
		title: "Assistente IA Xeito",
		welcomeMessage:
			"Ciao! Sono l'Assistente IA Xeito. Ti aiuto con domande su Xeito: come funziona, piani e prezzi, funzionalità e il tuo account. Per CV, lettere di presentazione, pratica di colloqui o negoziazione dello stipendio, usa gli strumenti di IA di Xeito.",
		inputPlaceholder: "Chiedi qualcosa su Xeito...",
		typing: "Sto pensando...",
		errorMessage:
			"Mi dispiace, ho difficoltà a rispondere alla tua domanda in questo momento. Per favore, riprova.",
		clearConversation: "Cancella conversazione",
		minimize: "Minimizza",
		askAnything: "Rispondo a domande su Xeito. Per CV, lettere, colloqui e stipendio, usa gli strumenti di IA.",
		messagesLeft: "messaggi rimasti oggi",
		limitReached: "Hai usato i messaggi di oggi. Si rinnovano domani.",
		quickActions: {
			pricing: "Quali sono i piani tariffari?",
			features: "Quali funzionalità offrite?",
			howItWorks: "Come funziona?",
			aiTools: "Quali strumenti IA sono disponibili?",
		},
	},
	nl: {
		title: "Xeito AI-assistent",
		welcomeMessage:
			"Hoi! Ik ben de Xeito AI-assistent. Ik help je met vragen over Xeito: hoe het werkt, abonnementen en prijzen, functies en je account. Voor je cv, sollicitatiebrieven, sollicitatietraining of salarisonderhandeling gebruik je de AI-tools van Xeito.",
		inputPlaceholder: "Vraag iets over Xeito...",
		typing: "Denken...",
		errorMessage:
			"Sorry, ik heb momenteel problemen met het beantwoorden van je vraag. Probeer het later opnieuw.",
		clearConversation: "Gesprek wissen",
		minimize: "Minimaliseren",
		askAnything: "Ik beantwoord vragen over Xeito. Voor cv, brieven, sollicitatiegesprekken en salaris gebruik je de AI-tools.",
		messagesLeft: "berichten over vandaag",
		limitReached: "Je hebt de berichten van vandaag gebruikt. Morgen kun je weer verder.",
		quickActions: {
			pricing: "Wat zijn de prijsplannen?",
			features: "Welke functies bieden jullie aan?",
			howItWorks: "Hoe werkt het?",
			aiTools: "Welke AI-tools zijn beschikbaar?",
		},
	},
	pt: {
		title: "Assistente IA do Xeito",
		welcomeMessage:
			"Olá! Sou o Assistente IA do Xeito. Ajudo-te com dúvidas sobre o Xeito: como funciona, planos e preços, funcionalidades e a tua conta. Para o teu CV, cartas de apresentação, prática de entrevistas ou negociação salarial, usa as ferramentas de IA do Xeito.",
		inputPlaceholder: "Pergunta sobre o Xeito...",
		typing: "A pensar...",
		errorMessage:
			"Peço desculpa, estou com dificuldades em responder à tua pergunta neste momento. Por favor, tenta novamente.",
		clearConversation: "Apagar conversa",
		minimize: "Minimizar",
		askAnything: "Respondo a dúvidas sobre o Xeito. Para CV, cartas, entrevistas e salário, usa as ferramentas de IA.",
		messagesLeft: "mensagens restantes hoje",
		limitReached: "Usaste as mensagens de hoje. Renovam-se amanhã.",
		quickActions: {
			pricing: "Quais são os planos de preços?",
			features: "Que funcionalidades oferecem?",
			howItWorks: "Como funciona?",
			aiTools: "Que ferramentas de IA estão disponíveis?",
		},
	},
	eu: {
		title: "Xeito AA Laguntzailea",
		welcomeMessage:
			"Kaixo! Xeitoren adimen artifizialeko (AA) laguntzailea naiz. Xeitori buruzko zalantzetan laguntzen dizut: nola dabilen, planak eta prezioak, funtzioak eta zure kontua. CV, aurkezpen gutunak, elkarrizketa praktika edo soldata negoziaziorako, erabili Xeitoren AA tresnak.",
		inputPlaceholder: "Galdetu Xeitori buruz...",
		typing: "Pentsatzen...",
		errorMessage:
			"Barkatu, zure galderari erantzuteko arazoak ditut oraintxe. Mesedez, saiatu berriro.",
		clearConversation: "Elkarrizketa ezabatu",
		minimize: "Minimizatu",
		askAnything: "Xeitori buruzko galderak erantzuten ditut. CV, gutunak, elkarrizketak eta soldatarako, erabili AA tresnak.",
		messagesLeft: "mezu geratzen dira gaur",
		limitReached: "Gaurko mezuak erabili dituzu. Bihar berritzen dira.",
		quickActions: {
			pricing: "Zein dira prezio-planak?",
			features: "Zer funtzionaltasun eskaintzen dituzue?",
			howItWorks: "Nola funtzionatzen du?",
			aiTools: "Zein AI tresna daude eskuragarri?",
		},
	},
	va: {
		title: "Assistent IA de Xeito",
		welcomeMessage:
			"Hola! Soc l'Assistent IA de Xeito. T'ajude amb dubtes sobre Xeito: com funciona, plans i preus, funcions i el teu compte. Per al CV, cartes de presentació, pràctica d'entrevistes o negociació salarial, utilitza les ferramentes d'IA de Xeito.",
		inputPlaceholder: "Pregunta sobre Xeito...",
		typing: "Pensant...",
		errorMessage:
			"Ho sent, tinc problemes per respondre la teva pregunta ara mateix. Si us plau, intenta-ho de nou.",
		clearConversation: "Esborrar conversa",
		minimize: "Minimitzar",
		askAnything: "Responc dubtes sobre Xeito. Per a CV, cartes, entrevistes i salari, utilitza les ferramentes d'IA.",
		messagesLeft: "missatges restants hui",
		limitReached: "Has usat els missatges d'hui. Es renoven demà.",
		quickActions: {
			pricing: "Quins són els plans de preus?",
			features: "Quines funcions ofereixen?",
			howItWorks: "Com funciona?",
			aiTools: "Quines eines d'IA hi ha?",
		},
	},
};

// Get the translation for the current locale
export function getTranslations(locale: string) {
	return translations[locale] || translations.en;
}

const AUTH_PATHS = ['/sign-in', '/sign-up']

// Daily limits by tier — must match apps/api/src/routes/chatbot.ts
const DAILY_LIMITS: Record<string, number> = {
	anonymous: 5,
	free: 10,
	job_tracker: 25,
	premium: 100,
};

export function CustomerServiceChatbot() {
	const location = useLocation()
	const isAuthPage = AUTH_PATHS.some(p => location.pathname.endsWith(p))
	const { user } = useAuth()
	// Read session cookie directly (getCookie is internal to auth-context).
	//
	// B-new-80: `xeito_session` is a plaintext mirror cookie set ONLY by the
	// OAuth callback (`routes/$locale/oauth.callback.tsx`) — email/password and
	// magic-link sign-ins never write it. For the majority of users `session`
	// here is always null, so the `Authorization: Bearer` header below was
	// never sent, and `/api/chatbot`'s `auth.api.getSession()` saw no session
	// at all: every non-OAuth signed-in user hit the endpoint as anonymous.
	// The fetch call below now also sends `credentials: "include"`, which
	// forwards BetterAuth's own httpOnly session cookie — set with
	// `sameSite: "lax"` + `crossSubDomainCookies` scoped to `.xeito.ai`
	// (`lib/auth.ts`), so it IS sent cross-subdomain between app.xeito.ai and
	// api.xeito.ai. That is the mechanism that actually authenticates most
	// users; the Bearer header stays as-is for OAuth sign-ins, matching the
	// documented dual-auth pattern in `apps/api/src/routes/referral.ts`
	// ("Bearer token in Authorization header, OR xeito_session cookie").
	const session = typeof document !== 'undefined'
		? document.cookie.match(/xeito_session=([^;]+)/)?.[1] ?? null
		: null;
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState<Message[]>([]);
	const [inputValue, setInputValue] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [locale, setLocale] = useState("en");
	const [remaining, setRemaining] = useState<number | null>(null);
	const [limitReached, setLimitReached] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Tier comes from the server, not the session. `AuthProvider` builds the
	// user as a 4-field literal ({id, email, name, image}) and the BetterAuth
	// client declares no additionalFields, so `user.subscriptionTier` was
	// `undefined` for every signed-in user — collapsing Premium (100/day) and
	// Job Tracker (25/day) to the free 10/day cap, and hiding the premium UI
	// from the people paying for it. Same class as B-new-83's two alert files;
	// this third site was not named in that row.
	const subscriptionQuery = trpc.user.getSubscription.useQuery(undefined, {
		enabled: !!user,
		retry: false,
	});
	const tier = user
		? (subscriptionQuery.data?.subscription?.tier ?? 'free')
		: 'anonymous';
	const dailyLimit = DAILY_LIMITS[tier] ?? DAILY_LIMITS.free;

	// Get translations based on current locale
	const t = getTranslations(locale);

	// B-new-152: react-i18next fallback for the Art. 50 chatbot disclosure
	// label. Aliased to i18nT so the local object-shape `t` above is
	// untouched. Reading via react-i18next keeps the disclosure copy in one
	// place (apps/web/src/i18n/translations/*.json → ai.disclosure.chatbot)
	// rather than requiring a parallel entry in this file's local
	// getTranslations() for all 8 (+ retired 5) locales.
	const { t: i18nT } = useTranslation();

	// Detect locale from localStorage or URL
	useEffect(() => {
		const detectedLocale =
			localStorage.getItem("locale") ||
			"en";
		setLocale(detectedLocale);
	}, []);

	// Load conversation from localStorage on mount
	useEffect(() => {
		const saved = localStorage.getItem(CHATBOT_MESSAGES_STORAGE_KEY);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed) && parsed.length > 0) {
					// Restore prior messages so re-opening the widget shows
					// history — but do NOT auto-expand the panel on mount.
					// Previously any returning visitor with saved messages
					// had the chat window force-open on every page load; the
					// only legitimate expand path is the floating button
					// below.
					setMessages(parsed);
				} else {
					setMessages([
						{
							id: "welcome",
							role: "assistant",
							content: t.welcomeMessage,
							timestamp: Date.now(),
						},
					]);
				}
			} catch {
				setMessages([
					{
						id: "welcome",
						role: "assistant",
						content: t.welcomeMessage,
						timestamp: Date.now(),
					},
				]);
			}
		} else {
			setMessages([
				{
					id: "welcome",
					role: "assistant",
					content: t.welcomeMessage,
					timestamp: Date.now(),
				},
			]);
		}
	}, [t]);

	// Scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	// Focus input when opened
	useEffect(() => {
		if (isOpen && isExpanded && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isOpen, isExpanded]);

	// B-new-1032: expose "is the chat panel open" on <body> so the Feedback
	// button (a sibling component, not a child) can hide itself while the
	// panel is open without duplicating this state — see the
	// `body[data-chat-open="true"] #feedback-button` rule in app.css. This
	// component remains the single source of truth for `isExpanded`.
	useEffect(() => {
		document.body.dataset.chatOpen = isExpanded ? "true" : "false";
		return () => {
			delete document.body.dataset.chatOpen;
		};
	}, [isExpanded]);

	const saveMessages = (newMessages: Message[]) => {
		setMessages(newMessages);
		localStorage.setItem(
			CHATBOT_MESSAGES_STORAGE_KEY,
			JSON.stringify(newMessages),
		);
	};

	const sendMessage = async (content: string) => {
		if (!content.trim() || isLoading || limitReached) return;

		const userMessage: Message = {
			id: Date.now().toString(),
			role: "user",
			content: content.trim(),
			timestamp: Date.now(),
		};

		const loadingMessage: Message = {
			id: "loading-" + Date.now(),
			role: "assistant",
			content: "",
			timestamp: Date.now(),
		};

		// Build history excluding the welcome message (role=assistant, id=welcome)
		const history = messages
			.filter((m) => m.id !== "welcome" && !m.id.startsWith("loading"))
			.map((m) => ({ role: m.role, content: m.content }));

		const newMessages = [...messages, userMessage, loadingMessage];
		saveMessages(newMessages);
		setInputValue("");
		setIsLoading(true);

		try {
			const headers: Record<string, string> = { "Content-Type": "application/json" };
			if (session) {
				headers["Authorization"] = `Bearer ${session}`;
			}

			// Use absolute URL so this works in production (Cloudflare Pages has no
			// proxy — relative /api/* would 404; VITE_API_URL points to the Worker).
			const apiBase = import.meta.env.VITE_API_URL ?? '';
			const response = await fetch(`${apiBase}/api/chatbot`, {
				method: "POST",
				headers,
				credentials: "include",
				body: JSON.stringify({ message: content.trim(), history, locale }),
			});

			if (response.status === 429) {
				setLimitReached(true);
				const finalMessages = newMessages.filter((msg) => msg.id !== loadingMessage.id);
				saveMessages(finalMessages);
				return;
			}

			if (!response.ok) {
				throw new Error("Failed to get response");
			}

			const data = await response.json() as { response?: string; remaining?: number | null };

			if (typeof data.remaining === 'number') {
				setRemaining(data.remaining);
				if (data.remaining <= 0) setLimitReached(true);
			}

			// Replace loading message with actual response
			const finalMessages = newMessages.map((msg) =>
				msg.id === loadingMessage.id
					? {
							...msg,
							id: Date.now().toString(),
							content: data.response || t.errorMessage,
						}
					: msg,
			);
			saveMessages(finalMessages);
		} catch (error) {
			console.error("Chat error:", error);
			const errorMessage: Message = {
				id: Date.now().toString(),
				role: "assistant",
				content: t.errorMessage,
				timestamp: Date.now(),
			};
			const finalMessages = [
				...newMessages.filter((msg) => msg.id !== loadingMessage.id),
				errorMessage,
			];
			saveMessages(finalMessages);
		} finally {
			setIsLoading(false);
		}
	};

	const handleQuickAction = (actionId: string) => {
		const actionLabels = t.quickActions;
		const actionKeys: Record<string, string> = {
			"pricing": actionLabels.pricing,
			"features": actionLabels.features,
			"how-it-works": actionLabels.howItWorks,
			"ai-features": actionLabels.aiFeatures,
		};
		sendMessage(actionKeys[actionId] || actionId);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		sendMessage(inputValue);
	};

	const clearConversation = () => {
		setMessages([
			{
				id: "welcome",
				role: "assistant",
				content: t.welcomeMessage,
				timestamp: Date.now(),
			},
		]);
		setIsExpanded(false);
		setIsOpen(false);
		setLimitReached(false);
	};

	if (isAuthPage) return null

	return (
		<div
			id="chatbot-launcher"
			className={cn(
				"fixed bottom-8 right-4 z-[60] flex flex-col items-end gap-2",
				isExpanded ? "w-80 sm:w-96" : "w-auto",
			)}
		>
			{/* Chat Window */}
			{isExpanded && (
				<div className="w-full bg-background rounded-lg shadow-2xl border border-border overflow-hidden">
					{/* Header — Art. 50 AI self-identification via the visible
					    "AI assistant" label next to the title (see
					    eu-compliance-reviewer verdict 2026-08-30). Prior top
					    banner removed in the same change. No external link: the
					    former https://xeito.es/docs/eu-ai-act-compliance URL is
					    dead (404), so the label carries an explanatory tooltip
					    (`ai.disclosure.tooltip`) instead; the compliance paper
					    trail lives in docs/13-compliance/EU-AI-ACT-COMPLIANCE.md. */}
					<div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
						<div className="flex items-center gap-2">
							<Bot className="h-5 w-5" aria-hidden="true" />
							{/* Operator 2026-09-15: one title, "Xeito AI Assistant", instead of
							    "Xeito Assistant · AI assistant". The title itself now carries the
							    Art. 50 self-identification ("AI" in every locale's name), so it
							    keeps the disclosure's role, tooltip, aria-label and test id. */}
							<span
								role="note"
								title={i18nT("ai.disclosure.tooltip")}
								aria-label={`${t.title} — ${i18nT("ai.disclosure.tooltip")}`}
								className="font-semibold"
								data-testid="ai-generated-disclosure"
							>
								{t.title}
							</span>
						</div>
						<div className="flex items-center gap-2">
							{remaining !== null && (
								<span className="text-xs text-primary-foreground/70">
									{remaining} {t.messagesLeft}
								</span>
							)}
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 hover:bg-primary/80"
								onClick={clearConversation}
								title={t.clearConversation}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 hover:bg-primary/80"
								onClick={() => setIsExpanded(false)}
								title={t.minimize}
							>
								<X className="h-4 w-4" />
							</Button>
						</div>
					</div>

					{/* Messages */}
					<div className="h-80 overflow-y-auto p-4 space-y-4 bg-card">
						{messages.map((message) => (
							<div
								key={message.id}
								className={cn(
									"flex gap-2",
									message.role === "user" ? "flex-row-reverse" : "flex-row",
								)}
							>
								<div
									className={cn(
										"max-w-[80%] rounded-lg px-3 py-2 text-sm",
										message.role === "user"
											? "bg-primary text-primary-foreground"
											: "bg-muted text-foreground",
									)}
								>
									{message.role === "assistant" &&
										message.id === "welcome" && (
											<Bot className="h-4 w-4 mb-1" />
										)}
									{isLoading && message.id.startsWith("loading") ? (
										<div className="flex items-center gap-1">
											<Loader2 className="h-3 w-3 animate-spin" />
											<span className="text-xs">{t.typing}</span>
										</div>
									) : message.role === "assistant" ? (
										// Assistant messages are Markdown per SYSTEM_PROMPT_FULL
										// ("Use markdown formatting (bold, lists) where it improves
										// clarity"). react-markdown sanitises HTML by default so
										// model output cannot inject arbitrary tags. GFM adds tables +
										// strikethrough — the model emits pricing tables that need
										// them. Tailwind Typography scoped locally rather than via
										// prose-* utilities to keep the message bubble compact.
										<div className="chatbot-markdown text-sm [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:my-1 [&_ol]:pl-4 [&_ol]:list-decimal [&_li]:my-0.5 [&_strong]:font-semibold [&_a]:text-primary [&_a]:underline [&_code]:rounded [&_code]:bg-background/60 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:my-2 [&_pre]:rounded [&_pre]:bg-background/60 [&_pre]:p-2 [&_pre]:text-xs [&_pre]:overflow-x-auto [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-2 [&_table]:my-2 [&_table]:w-full [&_table]:text-xs [&_th]:border-b [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border-b [&_td]:border-border/50 [&_td]:px-2 [&_td]:py-1">
											<ReactMarkdown
												remarkPlugins={[remarkGfm]}
												components={{
													// Force all model-emitted links to open in a new
													// tab + strip target-injection vectors. The model
													// only ever cites xeito.ai / help.xeito.ai but a
													// prompt-injected external URL would inherit the
													// same treatment.
													a: ({ href, children, ...props }) => (
														<a
															{...props}
															href={href}
															target="_blank"
															rel="noopener noreferrer"
														>
															{children}
														</a>
													),
												}}
											>
												{message.content}
											</ReactMarkdown>
										</div>
									) : (
										<p className="whitespace-pre-wrap">{message.content}</p>
									)}
								</div>
							</div>
						))}
						<div ref={messagesEndRef} />
					</div>

					{/* Quick Actions + hint */}
					{messages.length <= 2 && !isLoading && !limitReached && (
						<div className="px-4 py-2 border-t border-border bg-muted/30 space-y-2">
							<div className="flex flex-wrap gap-1">
								{QUICK_ACTIONS.map((action) => (
									<Button
										key={action.id}
										variant="outline"
										size="sm"
										className="text-xs h-7"
										onClick={() => handleQuickAction(action.id)}
									>
										{t.quickActions[action.id.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] || action.labelKey}
									</Button>
								))}
							</div>
							{/* Operator 2026-09-15: one chatbot for every tier — Xeito
							    questions only; career topics go to the AI tools. */}
							<p className="text-xs text-muted-foreground flex items-center gap-1">
								<Sparkles className="h-3 w-3 text-primary" />
								{t.askAnything}
							</p>
						</div>
					)}

					{/* Daily limit reached banner */}
					{limitReached && (
						<div className="px-4 py-3 border-t border-border bg-amber-50 dark:bg-amber-950/30">
							{/* B-new-1118: same daily limit for every tier, so no upgrade CTA —
							    upgrading would not give more chatbot messages. */}
							<p className="text-xs text-amber-800 dark:text-amber-200">{t.limitReached}</p>
						</div>
					)}

					{/* Input */}
					<form onSubmit={handleSubmit} className="p-4 border-t border-border">
						<div className="flex gap-2">
							<input
								ref={inputRef}
								type="text"
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								placeholder={limitReached ? "—" : t.inputPlaceholder}
								className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
								disabled={isLoading || limitReached}
							/>
							<Button
								type="submit"
								size="icon"
								aria-label={t.inputPlaceholder}
								className="h-9 w-9"
								disabled={isLoading || !inputValue.trim() || limitReached}
							>
								<Send className="h-4 w-4" />
							</Button>
						</div>
					</form>
				</div>
			)}

			{/* Floating Button. We use a plain native <button> + an inline SVG
			    instead of <Button> + lucide so:
			    - The global `.dark button { color: white }` rule in
			      styles/app.css can't fight us (we set stroke explicitly).
			    - lucide's currentColor inheritance doesn't matter (we
			      hard-code stroke=#0a0a0f / #ffffff). */}
			<button
				type="button"
				onClick={() => {
					if (!isExpanded) {
						setIsExpanded(true);
					}
					setIsOpen(true);
				}}
				aria-label={isOpen ? t.minimize : t.title}
				className={cn(
					"h-14 w-14 rounded-full shadow-lg transition-all duration-300 inline-flex items-center justify-center",
					isOpen
						? "bg-zinc-700 hover:bg-zinc-600"
						: "bg-[#00f0ff] hover:bg-[#00f0ff]/90",
				)}
			>
				{isOpen ? (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M18 6 6 18" />
						<path d="m6 6 12 12" />
					</svg>
				) : (
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0a0a0f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
					</svg>
				)}
			</button>
		</div>
	);
}
